import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ConfigurationTestResultId } from "@repo/app-types/entities/configurationTestResult";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import { useEffect } from "react";
import type { RootState } from "../../app/store.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { ConfigurationProposalStatus } from "./configurationProposalCard.js";

/** A row the open editor can show: a copilot proposal, or a saved test result on a playbook node. */
export type EditorViewedId = CopilotProposalId | ConfigurationTestResultId;

/**
 * The rows the open editor is showing — the proposals in its unsaved draft,
 * or the test result on its selected playbook node — and whether it is
 * saving them. The chat's proposal cards and deep-link lists read it.
 */
export interface EditorProposalsState {
  viewedIds: readonly EditorViewedId[];
  isSaving: boolean;
}

const initialState: EditorProposalsState = {
  viewedIds: [],
  isSaving: false,
};

const editorProposalsSlice = createSlice({
  name: "editorProposals",
  initialState,
  reducers: {
    reportEditorProposals: (
      _state,
      action: PayloadAction<EditorProposalsState>,
    ) => action.payload,
    clearEditorProposals: () => initialState,
  },
});

export const { reportEditorProposals, clearEditorProposals } =
  editorProposalsSlice.actions;
export const editorProposalsReducer = editorProposalsSlice.reducer;

/**
 * Reports the proposal the editor holds in its draft, if any, and whether it
 * is saving it, for as long as the editor is mounted.
 */
export function useReportEditorProposal(
  proposalId: CopilotProposalId | undefined,
  isSaving: boolean,
): void {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      reportEditorProposals({
        viewedIds: proposalId ? [proposalId] : [],
        isSaving,
      }),
    );
  }, [dispatch, proposalId, isSaving]);
  useEffect(
    () => () => {
      dispatch(clearEditorProposals());
    },
    [dispatch],
  );
}

/** Where a row stands in the open editor, if the editor holds it. */
export function selectEditorProposalStatus(
  state: RootState,
  viewedId: EditorViewedId,
):
  | typeof ConfigurationProposalStatus.Viewing
  | typeof ConfigurationProposalStatus.Saving
  | undefined {
  const { viewedIds, isSaving } = state.editorProposals;
  if (!viewedIds.includes(viewedId)) {
    return undefined;
  }
  return isSaving
    ? ConfigurationProposalStatus.Saving
    : ConfigurationProposalStatus.Viewing;
}
